/**
 * Geo API paths from Symfony (/geo-data/frontend-routes).
 *
 * Why server-resolved routes only here (not System.vue)?
 * Geo revise URLs embed typed path params (`/geo-data/revise/{entityType}/{entityId}`)
 * that already broke once when the backend renamed the single-account path. Fetching
 * Symfony path templates avoids repeating that drift. System admin endpoints
 * (`/db-dump`, `/schema/update`, …) are stable literal paths with query flags only —
 * hardcoding them is acceptable until they gain path params or a matching
 * frontend-routes endpoint.
 */

const ROUTES_ENDPOINT = '/geo-data/frontend-routes'
const REQUIRED_KEYS = ['reviseEntity', 'reviseBulk', 'overview', 'debugQualification']
const RETRY_BACKOFF_MS = 5000

let cachedPaths = null
let loadPromise = null
let failedUntil = 0

function fillTemplate (template, params = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    if (params[key] == null || params[key] === '') {
      throw new Error(`Missing path param "${key}" for template ${template}`)
    }
    return encodeURIComponent(String(params[key]))
  })
}

function withQuery (path, query = {}) {
  const params = new URLSearchParams(query)
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export function loadGeoPaths (http) {
  if (cachedPaths) {
    return Promise.resolve(cachedPaths)
  }
  if (loadPromise) {
    return loadPromise
  }
  if (Date.now() < failedUntil) {
    return Promise.reject(new Error('Geo frontend-routes recently failed, retrying shortly'))
  }

  loadPromise = http.get(ROUTES_ENDPOINT)
    .then((response) => {
      const paths = response?.data?.paths
      if (!paths || typeof paths !== 'object') {
        throw new Error('Geo frontend-routes response missing paths')
      }
      for (const key of REQUIRED_KEYS) {
        if (typeof paths[key] !== 'string' || paths[key].length === 0) {
          throw new Error(`Geo frontend-routes missing path "${key}"`)
        }
      }
      failedUntil = 0
      cachedPaths = paths
      return cachedPaths
    })
    .catch((err) => {
      failedUntil = Date.now() + RETRY_BACKOFF_MS
      throw err
    })
    .finally(() => {
      loadPromise = null
    })

  return loadPromise
}

export async function geoReviseEntityPath (http, entityType, entityId) {
  const paths = await loadGeoPaths(http)
  return fillTemplate(paths.reviseEntity, { entityType, entityId })
}

export async function geoReviseBulkPath (http, query = {}) {
  const paths = await loadGeoPaths(http)
  return withQuery(paths.reviseBulk, query)
}

export async function geoOverviewPath (http, query = {}) {
  const paths = await loadGeoPaths(http)
  return withQuery(paths.overview, query)
}

export async function geoDebugQualificationPath (http, query = {}) {
  const paths = await loadGeoPaths(http)
  return withQuery(paths.debugQualification, query)
}
